import { useState, type ReactNode } from 'react';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { cn } from '@/lib/utils';
import { useMnaSources } from '../lib/api';
import type { SourceDto, SourceType } from '../types';
import { Flag } from './_shared';

// Tab 6 — Sources. The catalog behind every M&A fact (principle: every fact carries its
// source), grouped by publisher type — the WWD Sources idiom. Paywalled rows are catalog
// entries only, never scraped.

const TYPE_ORDER: SourceType[] = ['government', 'court', 'registry', 'company', 'ratings_agency', 'media', 'other'];

const TYPE_LABELS: Record<SourceType, string> = {
  government: 'Government / regulators',
  court: 'Courts & claims agents',
  registry: 'Company registries',
  company: 'Company sources',
  ratings_agency: 'Ratings agencies',
  media: 'Trade media',
  other: 'Other',
};

const TYPE_BADGE_LABELS: Record<SourceType, string> = {
  government: 'Government',
  court: 'Court',
  registry: 'Registry',
  company: 'Company',
  ratings_agency: 'Ratings',
  media: 'Media',
  other: 'Other',
};

type Access = SourceDto['access'];

const ACCESS_STYLES: Record<Access, string> = {
  free: 'border-border text-muted',
  registration: 'border-accent-info text-accent-info',
  paywalled: 'border-accent-warn text-accent-warn',
};

const filterInputClass = cn(
  'bg-surface-elevated border border-border rounded-(--radius-sm) px-3 py-2 text-sm min-h-11',
  'font-(family-name:--font-body) focus:outline-2 focus:outline-offset-2 focus:outline-accent'
);

function matchesFilter(s: SourceDto, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (needle === '') return true;
  return [s.name, s.slug, s.jurisdiction, s.notes, TYPE_LABELS[s.type], s.access]
    .some((field) => field?.toLowerCase().includes(needle));
}

export default function Sources() {
  const { data: sources, isLoading, error } = useMnaSources();
  const [filter, setFilter] = useState('');

  if (isLoading) return <LoadingSkeleton lines={8} />;
  if (error || !sources) return <ErrorState message={(error as Error | null)?.message} />;

  const filtered = sources.filter((s) => matchesFilter(s, filter));
  const groups = TYPE_ORDER.map((type) => ({
    type,
    rows: filtered.filter((s) => s.type === type),
  })).filter((g) => g.rows.length > 0);

  return (
    <div className="stagger">
      <Card>
        <CardTitle>Sources</CardTitle>
        <CardDescription className="mt-2">
          Every fact in the M&amp;A Radar carries its source. This catalog is the complete source
          list — registries and courts first, trade media last.
        </CardDescription>
      </Card>

      <Card>
        {sources.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <input
              type="search"
              className={cn(filterInputClass, 'w-72 max-w-full')}
              value={filter}
              placeholder="Filter by name, type or jurisdiction"
              aria-label="Filter sources"
              onChange={(e) => setFilter(e.target.value)}
            />
            <span className="text-xs text-muted">
              {filtered.length} of {sources.length} sources
            </span>
          </div>
        )}
        {sources.length === 0 ? (
          <CardDescription>No sources cataloged yet.</CardDescription>
        ) : filtered.length === 0 ? (
          <CardDescription>No sources match the filter.</CardDescription>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-accent">
                  <Th>Name</Th>
                  <Th>Type</Th>
                  <Th>Access</Th>
                  <Th center>Jurisdiction</Th>
                  <Th>Notes</Th>
                </tr>
              </thead>
              {groups.map((g) => (
                <tbody key={g.type}>
                  <tr>
                    <th
                      scope="rowgroup"
                      colSpan={5}
                      className="pt-5 pb-2 px-3 text-left font-(family-name:--font-ui) text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-muted"
                    >
                      {TYPE_LABELS[g.type]}
                    </th>
                  </tr>
                  {g.rows.map((s) => (
                    <SourceRow key={s.slug} source={s} />
                  ))}
                </tbody>
              ))}
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function Th({ children, center }: { children: ReactNode; center?: boolean }) {
  return (
    <th
      className={cn(
        'py-2 px-3 text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui)',
        center ? 'text-center' : 'text-left'
      )}
    >
      {children}
    </th>
  );
}

function Badge({ className, children }: { className: string; children: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-block whitespace-nowrap rounded-(--radius-sm) border bg-surface-elevated px-2 py-0.5',
        'font-(family-name:--font-ui) text-[0.65rem] font-medium uppercase tracking-[0.12em]',
        className
      )}
    >
      {children}
    </span>
  );
}

function SourceRow({ source }: { source: SourceDto }) {
  return (
    <tr className="border-b border-border align-top hover:bg-surface-hover">
      <td className="py-3 px-3">
        <a href={source.url} target="_blank" rel="noreferrer" className="font-medium text-accent hover:underline">
          {source.name}
        </a>
      </td>
      <td className="py-3 px-3">
        <Badge className="border-border text-muted">{TYPE_BADGE_LABELS[source.type]}</Badge>
      </td>
      <td className="py-3 px-3">
        <Badge className={ACCESS_STYLES[source.access]}>{source.access}</Badge>
      </td>
      <td className="py-3 px-3 text-center">
        {source.jurisdiction ? <Flag countryCode={source.jurisdiction} /> : <span className="text-muted">—</span>}
      </td>
      <td className="py-3 px-3 text-muted">{source.notes ?? '—'}</td>
    </tr>
  );
}
